import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ServiceIcon from '../components/ServiceIcon';
import FAQSection from '../components/FAQSection';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { SITE_CONFIG } from '../data/seoConfig';
import { INDUSTRY_FAQS } from '../data/faqs';
import { INDUSTRIES, SERVICES } from '../mock';

const INDUSTRY_DETAILS = {
  'ecommerce-retail': {
    heroDesc: 'DTC growth built on clean signal, creative velocity, and full-funnel media that maximizes ROAS.',
    challenges: ['iOS signal loss', 'Creative fatigue at scale', 'Rising CAC across Meta', 'Shopify + warehouse data silos'],
    approach: ['Google Shopping & Performance Max', 'Meta Advantage+ Shopping automation', 'Dynamic product remarketing', 'Abandoned cart recovery sequences'],
    services: ['paid-media-advertising', 'social-media-management', 'website-design-development', 'ai-automation'],
  },
  'saas-technology': {
    heroDesc: 'Pipeline generation for PLG, freemium, and enterprise SaaS motions — tied to ARR, not vanity metrics.',
    challenges: ['Attribution from signup to closed-won', 'Balancing PLG + sales-led', 'LinkedIn CPMs vs. quality', 'SEO in the LLM era'],
    approach: ['MQL/SQL/opportunity modeling', 'LinkedIn + Google demand programs', 'LLM + entity-based SEO', 'ABM overlays'],
    services: ['paid-media-advertising', 'search-engine-optimization', 'llmo-optimization', 'ai-automation'],
  },
  'healthcare-wellness': {
    heroDesc: 'Compliant patient acquisition with HIPAA-aware advertising across search, social, and programmatic.',
    challenges: ['HIPAA compliance', 'Regulated ad platforms', 'Local + national blend', 'Show-rate vs. lead-rate'],
    approach: ['Local Services Ads for healthcare', 'Location-based mobile targeting', 'Appointment booking CRO', 'Reputation-building display'],
    services: ['paid-media-advertising', 'search-engine-optimization', 'website-design-development', 'ai-automation'],
  },
  'professional-services': {
    heroDesc: 'High-trust growth for consulting, accounting, and specialized service firms in regulated environments.',
    challenges: ['Compliance-first messaging', 'Long trust-building cycles', 'Local SEO across offices', 'Referral vs. paid balance'],
    approach: ['Local SEO + programmatic pages', 'Consent-mode compliant tracking', 'Content authority programs', 'Reputation + review systems'],
    services: ['search-engine-optimization', 'llmo-optimization', 'ai-automation', 'website-design-development'],
  },
  'real-estate': {
    heroDesc: 'Multi-market lead generation for developers, brokers, and home builders — with the tracking to prove it.',
    challenges: ['Long, offline sales cycles', 'Multi-project attribution', 'Local + national campaign balance', 'Rising CPMs in home categories'],
    approach: ['Geo-tiered Google & Meta programs', 'Offline conversion imports from CRM', 'Landing page CRO for each project', 'Weekly cost-per-appointment reporting'],
    services: ['paid-media-advertising', 'social-media-management', 'website-design-development', 'ai-automation'],
  },
  'finance-insurance': {
    heroDesc: 'Regulated-industry growth programs with rigorous compliance, CRO-first pages, and trust-building content.',
    challenges: ['Regulated ad networks', 'Compliance across markets', 'High CPMs on core keywords', 'Trust and reputation building'],
    approach: ['Compliant paid programs', 'Content authority + LLMO', 'CRO on quote-request pages', 'Multi-region localization'],
    services: ['paid-media-advertising', 'search-engine-optimization', 'llmo-optimization', 'website-design-development'],
  },
  'education-elearning': {
    heroDesc: 'Enrollment growth for online academies, universities, and edtech platforms with global reach.',
    challenges: ['Enrollment seasonality', 'Multi-country compliance', 'High application drop-off', 'Long consideration cycles'],
    approach: ['Full-funnel paid + retargeting', 'Application-flow CRO', 'Content authority + LLMO', 'AI chat for admissions'],
    services: ['paid-media-advertising', 'search-engine-optimization', 'ai-automation', 'website-design-development'],
  },
  'hospitality-travel': {
    heroDesc: 'Bookings and brand growth for hotels, resorts, tour operators, and destination marketing organizations.',
    challenges: ['Seasonality management', 'OTA vs. direct-booking', 'Global multi-language creative', 'Real-time inventory pricing'],
    approach: ['Direct-booking Google & Meta', 'Seasonality-aware pacing', 'Multi-language creative', 'Video-led brand campaigns'],
    services: ['paid-media-advertising', 'video-production', 'social-media-management', 'website-design-development'],
  },
  'manufacturing-b2b': {
    heroDesc: 'Long-cycle B2B demand generation, ABM, and multi-stakeholder pipelines tied to closed-won revenue.',
    challenges: ['Long cycles, small buying committees', 'Content credibility', 'Attribution across touchpoints', 'Sales + marketing alignment'],
    approach: ['ABM tiered account programs', 'CRM-driven audience matching', 'Thought leadership content', 'Full-funnel dashboards'],
    services: ['paid-media-advertising', 'search-engine-optimization', 'ai-automation', 'llmo-optimization'],
  },
  'legal-services': {
    heroDesc: 'High-value case leads across Google Search, Local Services Ads, and targeted display for legal firms.',
    challenges: ['Bar association compliance', 'High CPCs on core terms', 'Local SEO across offices', 'Case-quality vs. quantity'],
    approach: ['LSA with Google Screened', 'High-intent search programs', 'Local SEO at scale', 'CRO on intake forms'],
    services: ['paid-media-advertising', 'search-engine-optimization', 'website-design-development', 'ai-automation'],
  },
  'automotive': {
    heroDesc: 'Dealer group and aftermarket growth with local + national blended media and full inventory feeds.',
    challenges: ['Dealer network coordination', 'Inventory-driven ads', 'Local + national blend', 'Attribution to walk-ins'],
    approach: ['Inventory-driven Google Ads', 'YouTube + CTV brand campaigns', 'Local geo-targeted Meta', 'Call + form tracking'],
    services: ['paid-media-advertising', 'video-production', 'ai-automation', 'website-design-development'],
  },
  'food-beverage': {
    heroDesc: 'DTC beverages, CPG brands, and restaurant groups scaling with paid, creative, and retail-media.',
    challenges: ['Retail vs. DTC balance', 'Creative iteration speed', 'Amazon + retail media', 'Multi-SKU catalog complexity'],
    approach: ['Meta + TikTok creative velocity', 'Amazon PPC + retail media', 'Influencer + UGC amplification', 'Full-funnel measurement'],
    services: ['paid-media-advertising', 'social-media-management', 'video-production', 'ai-automation'],
  },
};

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = INDUSTRIES.find((i) => i.slug === slug);
  const details = INDUSTRY_DETAILS[slug];
  const canonical = ind ? `${SITE_CONFIG.url}/industries/${ind.slug}` : undefined;

  useSEO(
    ind
      ? {
          title: `Digital Marketing Agency for ${ind.title} — Ransen`,
          description: `${details?.heroDesc || ind.blurb} Ransen delivers digital marketing programs tailored for ${ind.title.toLowerCase()} brands worldwide.`,
          canonical,
          keywords: `digital marketing agency for ${ind.title.toLowerCase()}, ${ind.title.toLowerCase()} marketing agency, ${ind.title.toLowerCase()} SEO, ${ind.title.toLowerCase()} PPC, ${ind.title.toLowerCase()} paid media`,
        }
      : {}
  );

  useJsonLd(
    ind ? `industry-schema-${ind.slug}` : 'industry-schema',
    ind
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: `Digital Marketing for ${ind.title}`,
            description: details?.heroDesc || ind.blurb,
            url: canonical,
            provider: { '@id': `${SITE_CONFIG.url}/#organization` },
            audience: { '@type': 'BusinessAudience', audienceType: ind.title },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
              { '@type': 'ListItem', position: 2, name: 'Industries', item: `${SITE_CONFIG.url}/industries` },
              { '@type': 'ListItem', position: 3, name: ind.title, item: canonical },
            ],
          },
        ]
      : null
  );

  if (!ind) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>Industry not found</h1>
        <Link to='/industries' className='mt-6 inline-block text-brand'>Back to industries</Link>
      </div>
    );
  }

  const relatedServices = (details?.services || []).map((s) => SERVICES.find((sv) => sv.slug === s)).filter(Boolean);

  return (
    <div>
      <PageHeader
        eyebrow={`Industry · ${ind.title}`}
        title={<><span className='text-white'>Marketing for </span><span className='text-brand-gradient'>{ind.title}</span></>}
        description={details?.heroDesc || ind.blurb}
        crumbs={[{ label: 'Industries', to: '/industries' }, { label: ind.title }]}
      />

      <section className='py-16'>
        <div className='max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6'>
          <div className='glass-card rounded-2xl p-7'>
            <div className='eyebrow-pill' style={{ fontSize: 11 }}>Common Challenges</div>
            <h3 className='mt-4 font-heading text-2xl font-bold text-white'>What holds category leaders back</h3>
            <ul className='mt-5 space-y-3'>
              {(details?.challenges || []).map((c) => (
                <li key={c} className='flex gap-3 text-white/75 text-[14.5px]'>
                  <div className='w-1.5 h-1.5 rounded-full bg-accent-pink mt-2 shrink-0' />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className='glass-card rounded-2xl p-7'>
            <div className='eyebrow-pill' style={{ fontSize: 11 }}>Our Approach</div>
            <h3 className='mt-4 font-heading text-2xl font-bold text-white'>How Ransen solves it</h3>
            <ul className='mt-5 space-y-3'>
              {(details?.approach || []).map((c) => (
                <li key={c} className='flex gap-3 text-white/80 text-[14.5px]'>
                  <Check className='w-4 h-4 text-brand mt-1 shrink-0' />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {relatedServices.length > 0 && (
        <section className='py-16 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Services we deploy for {ind.title}</h3>
            <div className='mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
              {relatedServices.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className='glass-card rounded-2xl p-5 group'>
                  <div className='w-10 h-10 rounded-lg bg-gradient-to-br from-brand-dark to-brand flex items-center justify-center shadow-md shadow-brand/30'>
                    <ServiceIcon name={s.icon} className='w-5 h-5 text-white' />
                  </div>
                  <div className='mt-4 font-heading font-bold text-white text-[15px]'>{s.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className='py-20 border-t border-brand/10 text-center'>
        <div className='max-w-3xl mx-auto px-6'>
          <h3 className='font-heading text-3xl md:text-4xl font-bold text-white'>Ready to unlock growth in {ind.title}?</h3>
          <Link to='/contact' className='mt-8 btn-primary text-white font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-2'>
            Book a Strategy Call <ArrowRight className='w-4 h-4' />
          </Link>
        </div>
      </section>

      {(INDUSTRY_FAQS[ind.slug] || []).length > 0 && (
        <FAQSection
          items={INDUSTRY_FAQS[ind.slug]}
          schemaId={`industry-faq-${ind.slug}`}
          eyebrow={ind.title}
          title={`${ind.title} — FAQ`}
          subtitle={`Answers to the most common questions about digital marketing for ${ind.title.toLowerCase()} brands.`}
        />
      )}
    </div>
  );
}
